import { cn } from 'oddle-shop-monorepo.design-system'

// Enhanced RGBA interface with additional utility
export interface RGBAColor {
  r: number
  g: number
  b: number
  a?: number
}

export const getColorString = (color: RGBAColor): string => {
  const { r, g, b, a = 1 } = color
  return a === 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`
}

// Color constants mapped to Tailwind custom color names
export const colorConstants = {
  oddle: '#FF485B',
  oddleHover: '#E64152',
  facebook: '#3A589B',
  facebookHover: '#344F8B',
  google: '#DF4A32',
  googleHover: '#C8422C',
  white: '#FFFFFF',
  black: '#000000',
  matteBlack: '#393939',
  lightGray: '#D6D6D6',
  lightestGray: '#F6F6F6',
  gray: '#BBBBBB',
  green: '#067E30',
  lightGreen: '#DDF7E6',
  blue: '#0C62CA',
  lightBlue: '#D7E9FF',
  red: '#D62105',
  lightRed: '#FFEEEB',
  yellow: '#FFCF35',
  lightYellow: '#FFF1BB',
  transparent: 'transparent',
  defaultText: '#515151',
  oddleEats: '#350893',
  progressBarError: '#D0021B',
  progressBarColor: '#27CA27',
  mainVoucherColor: '#0A3040',
} as const

export const colors = {
  brandPrimary: colorConstants.oddle,
  brandPrimaryDark: colorConstants.oddleHover,
  brandPrimaryDarkest: '#992B37',
  brandPrimaryLight: '#FF6D7C',
  brandPrimaryLightest: '#FFE3E6',
  brandSecondary: '#23537E',
  heading: colorConstants.matteBlack,
  text: colorConstants.defaultText,
  buttonText: colorConstants.white,
  background: '#F1F1F1',
  separator: colorConstants.lightGray,
  container: colorConstants.white,
  disabled: colorConstants.gray,
  headerBackground: colorConstants.oddle,
  headerNav: colorConstants.white,
  footerBackground: '#16688a',
  footerNav: colorConstants.white,
  toggleOff: colorConstants.gray,
  toggleOn: '#17d254',
  promotionFilmstripPlaceholder: '#F9FAFC',
  alertErrorLight: colorConstants.lightRed,
  alertError: colorConstants.red,
  alternativeHighlightColor: '#2B85EF',
}

// Tailwind class mappings for brand colors
export const tailwindColorClasses = {
  // Background colors
  bg: {
    oddle: 'bg-[#FF485B]',
    oddleHover: 'bg-[#E64152]',
    facebook: 'bg-[#3A589B]',
    facebookHover: 'bg-[#344F8B]',
    google: 'bg-[#DF4A32]',
    googleHover: 'bg-[#C8422C]',
    matteBlack: 'bg-[#393939]',
    lightGray: 'bg-[#D6D6D6]',
    lightestGray: 'bg-[#F6F6F6]',
    gray: 'bg-[#BBBBBB]',
    green: 'bg-[#067E30]',
    lightGreen: 'bg-[#DDF7E6]',
    blue: 'bg-[#0C62CA]',
    lightBlue: 'bg-[#D7E9FF]',
    red: 'bg-[#D62105]',
    lightRed: 'bg-[#FFEEEB]',
    yellow: 'bg-[#FFCF35]',
    lightYellow: 'bg-[#FFF1BB]',
    defaultText: 'bg-[#515151]',
    oddleEats: 'bg-[#350893]',
    progressBarError: 'bg-[#D0021B]',
    progressBarColor: 'bg-[#27CA27]',
    mainVoucherColor: 'bg-[#0A3040]',
  },
  // Text colors
  text: {
    oddle: 'text-[#FF485B]',
    oddleHover: 'text-[#E64152]',
    facebook: 'text-[#3A589B]',
    facebookHover: 'text-[#344F8B]',
    google: 'text-[#DF4A32]',
    googleHover: 'text-[#C8422C]',
    matteBlack: 'text-[#393939]',
    lightGray: 'text-[#D6D6D6]',
    lightestGray: 'text-[#F6F6F6]',
    gray: 'text-[#BBBBBB]',
    green: 'text-[#067E30]',
    lightGreen: 'text-[#DDF7E6]',
    blue: 'text-[#0C62CA]',
    lightBlue: 'text-[#D7E9FF]',
    red: 'text-[#D62105]',
    lightRed: 'text-[#FFEEEB]',
    yellow: 'text-[#FFCF35]',
    lightYellow: 'text-[#FFF1BB]',
    defaultText: 'text-[#515151]',
    oddleEats: 'text-[#350893]',
    progressBarError: 'text-[#D0021B]',
    progressBarColor: 'text-[#27CA27]',
    mainVoucherColor: 'text-[#0A3040]',
  },
  // Border colors
  border: {
    oddle: 'border-[#FF485B]',
    oddleHover: 'border-[#E64152]',
    facebook: 'border-[#3A589B]',
    facebookHover: 'border-[#344F8B]',
    google: 'border-[#DF4A32]',
    googleHover: 'border-[#C8422C]',
    matteBlack: 'border-[#393939]',
    lightGray: 'border-[#D6D6D6]',
    lightestGray: 'border-[#F6F6F6]',
    gray: 'border-[#BBBBBB]',
    green: 'border-[#067E30]',
    lightGreen: 'border-[#DDF7E6]',
    blue: 'border-[#0C62CA]',
    lightBlue: 'border-[#D7E9FF]',
    red: 'border-[#D62105]',
    lightRed: 'border-[#FFEEEB]',
    yellow: 'border-[#FFCF35]',
    lightYellow: 'border-[#FFF1BB]',
    defaultText: 'border-[#515151]',
    oddleEats: 'border-[#350893]',
    progressBarError: 'border-[#D0021B]',
    progressBarColor: 'border-[#27CA27]',
    mainVoucherColor: 'border-[#0A3040]',
  },
} as const

export const states = {
  success: 'success',
  informative: 'informative',
  warning: 'warning',
  error: 'error',
  draft: 'draft',
} as const

export const stateThemes = {
  dark: 'dark',
  light: 'light',
} as const

export type StateType = keyof typeof states
export type ThemeType = keyof typeof stateThemes

// Tailwind classes for state-based styling
export const getStateClasses = (
  state: StateType,
  theme: ThemeType = 'light',
  type: 'bg' | 'text' | 'border' = 'bg'
): string => {
  const stateClassMap = {
    success: {
      bg: {
        dark: tailwindColorClasses.bg.green,
        light: tailwindColorClasses.bg.lightGreen,
      },
      text: {
        dark: tailwindColorClasses.text.lightGreen,
        light: tailwindColorClasses.text.green,
      },
      border: {
        dark: tailwindColorClasses.border.green,
        light: tailwindColorClasses.border.lightGreen,
      },
    },
    informative: {
      bg: {
        dark: tailwindColorClasses.bg.blue,
        light: tailwindColorClasses.bg.lightBlue,
      },
      text: {
        dark: tailwindColorClasses.text.lightBlue,
        light: tailwindColorClasses.text.blue,
      },
      border: {
        dark: tailwindColorClasses.border.blue,
        light: tailwindColorClasses.border.lightBlue,
      },
    },
    warning: {
      bg: {
        dark: tailwindColorClasses.bg.yellow,
        light: tailwindColorClasses.bg.lightYellow,
      },
      text: { dark: 'text-black', light: 'text-black' },
      border: {
        dark: tailwindColorClasses.border.yellow,
        light: tailwindColorClasses.border.lightYellow,
      },
    },
    error: {
      bg: {
        dark: tailwindColorClasses.bg.red,
        light: tailwindColorClasses.bg.lightRed,
      },
      text: {
        dark: tailwindColorClasses.text.lightRed,
        light: tailwindColorClasses.text.red,
      },
      border: {
        dark: tailwindColorClasses.border.red,
        light: tailwindColorClasses.border.lightRed,
      },
    },
    draft: {
      bg: { dark: 'bg-transparent', light: 'bg-transparent' },
      text: { dark: 'text-black', light: 'text-white' },
      border: { dark: 'border-black', light: 'border-white' },
    },
  }

  return stateClassMap[state][type][theme]
}

// Utility function to combine multiple state classes
export const stateColors = (
  state: StateType,
  theme: ThemeType = 'light',
  additionalClasses?: string
): string => {
  return cn(
    getStateClasses(state, theme, 'bg'),
    getStateClasses(state, theme, 'text'),
    getStateClasses(state, theme, 'border'),
    additionalClasses
  )
}

// Helper function to get color class by name
export const getColorClass = (
  colorName: keyof typeof tailwindColorClasses.bg,
  type: 'bg' | 'text' | 'border' = 'bg'
): string => {
  return tailwindColorClasses[type][colorName]
}
